import { t } from "i18next";
import React, { useState } from "react";

import { DEFAULT_API_CONFIG } from "../../api/api-config";
import useDataFetch from "../../hooks/useDataFetch";
import { IOrder, IOrderline } from "../../utils/data-types";
import { formatDate, formatPrice } from "../../utils/functions";
import { Text } from "../text/Text";
import "./OrderlineView.scss";

export const OrderlineView = ({
  orderId,
  customerId,
}: {
  orderId: number | undefined;
  customerId: number | undefined;
  setIsOrderlineView: (isOrderlineView: boolean) => void;
}) => {
  const [orderlineUrl] = useState<string>(
    DEFAULT_API_CONFIG.url + "/crm/ordrelinjer/get/" + orderId
  );
  const [orderlineData] = useDataFetch<IOrderline[]>(orderlineUrl, "GET");

  const [orderUrl] = useState<string>(
    DEFAULT_API_CONFIG.url + "/crm/ordre/get/" + customerId
  );
  const [orderData] = useDataFetch<IOrder[]>(orderUrl, "GET");

  const order = orderData?.filter((order) => order.ordreid === orderId);

  const totalSum = orderlineData?.reduce((acc, curr) => acc + curr.linjesum, 0);
  const totalMva = orderlineData?.reduce((acc, curr) => acc + curr.sumvat, 0);

  return (
    <div className="orderline-view">
      <div className="orderline-view__header">
        <Text
          text={t("pages.orderlineView.orderNo") + " " + orderId?.toString()}
          color="dark"
          fontWeight={600}
        />
      </div>
      <div className="orderline-view__content">
        <div className="orderline-view__content__meta">
          {order && (
            <div className="orderline-view__content__meta__left">
              <Text text={order[0].kundenavn} size="xxs" fontWeight={600} />
              <Text
                text={t("pages.orderlineView.customerId") + order[0].kundeid}
                size="xxxs"
              />
              <Text text={order[0].adresse} size="xxxs" />
              <Text text={order[0].adresse2} size="xxxs" />
              <Text
                text={order[0].postnummer + " " + order[0].by}
                size="xxxs"
              />
              <Text text={t("pages.orderlineView.norway")} size="xxxs" />
            </div>
          )}
          <div className="orderline-view__content__meta__middle">
            <div className="orderline-view__content__meta__middle__key">
              <Text text={t("pages.orderlineView.theirRef")} size="xxxs" />
              <Text text={t("pages.orderlineView.orderDate")} size="xxxs" />
              <Text text={t("pages.orderlineView.deliveryDate")} size="xxxs" />
              <Text
                text={t("pages.orderlineView.readyForBilling")}
                size="xxxs"
              />
            </div>
            {order && (
              <div className="orderline-view__content__meta__middle__value">
                <div className="empty-text-align">
                  <Text text={order[0].deres_ref} size="xxxs" />
                </div>
                <div className="empty-text-align">
                  <Text text={formatDate(order[0].ordre_dato)} size="xxxs" />
                </div>
                <div className="empty-text-align">
                  <Text
                    text={formatDate(order[0].leverings_dato)}
                    size="xxxs"
                  />
                </div>
                <div className="empty-text-align">
                  <Text text={order[0].status} size="xxxs" />
                </div>
              </div>
            )}
          </div>
          <div className="orderline-view__content__meta__right">
            <div className="orderline-view__content__meta__right__key">
              <Text text={t("pages.orderlineView.totalExVat")} size="xxxs" />
              <Text text={t("pages.orderlineView.vat")} size="xxxs" />
              <Text text={t("pages.orderlineView.totalSum")} size="xxxs" />
            </div>
            {order && totalSum && totalMva && (
              <div className="orderline-view__content__meta__right__value">
                <div className="empty-text-align">
                  <Text text={formatPrice(totalSum - totalMva)} size="xxxs" />
                </div>
                <div className="empty-text-align">
                  <Text text={formatPrice(totalMva)} size="xxxs" />
                </div>
                <div className="empty-text-align">
                  <Text text={formatPrice(totalSum)} size="xxxs" />
                </div>
              </div>
            )}
          </div>
        </div>
        {/* ------------------------------  */}
        <div className="orderline-table" id="table">
          <div className="orderline-table__content">
            <div className="table-header">
              <div className="table-cell">
                <Text text={t("pages.orderlineView.itemNr")} size="xxxs" />
              </div>
              <div className="table-cell">
                <Text text={t("pages.orderlineView.text")} size="xxxs" />
              </div>
              <div className="table-cell">
                <Text text={t("pages.orderlineView.quantity")} size="xxxs" />
              </div>
              <div className="table-cell">
                <Text text={t("pages.orderlineView.unit")} size="xxxs" />
              </div>
              <div className="table-cell">
                <Text text={t("pages.orderlineView.price")} size="xxxs" />
              </div>
              <div className="table-cell">
                <Text text={t("pages.orderlineView.vatCode")} size="xxxs" />
              </div>
              <div className="table-cell">
                <Text
                  text={t("pages.orderlineView.discountPercent")}
                  size="xxxs"
                />
              </div>
              <div className="table-cell">
                <Text text={t("pages.orderlineView.sum")} size="xxxs" />
              </div>
            </div>
            <div className="orderline-table__rows">
              {orderlineData &&
                orderlineData.map((row, rowIndex: number) => (
                  <div className="table-row" key={rowIndex}>
                    <div className="table-cell">
                      <Text
                        text={orderlineData[rowIndex].artikkel}
                        size="xxs"
                      />
                    </div>
                    <div className="table-cell">
                      <Text text={orderlineData[rowIndex].tekst} size="xxs" />
                    </div>
                    <div className="table-cell">
                      <Text
                        text={formatPrice(
                          orderlineData[rowIndex].mengde
                        ).toString()}
                        size="xxs"
                      />
                    </div>
                    <div className="table-cell">
                      <Text text={orderlineData[rowIndex].enhet} size="xxs" />
                    </div>
                    <div className="table-cell">
                      <Text
                        text={formatPrice(orderlineData[rowIndex].pris)}
                        size="xxs"
                      />
                    </div>
                    <div className="table-cell">
                      <Text
                        text={
                          "0: + " + orderlineData[rowIndex].mvaprosent + "% MVA"
                        }
                        size="xxs"
                      />
                    </div>
                    <div className="table-cell">
                      <Text
                        text={orderlineData[rowIndex].rabattprosent.toString()}
                        size="xxs"
                      />
                    </div>
                    <div className="table-cell">
                      <Text
                        text={formatPrice(orderlineData[rowIndex].sumnet)}
                        size="xxs"
                      />
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
